"use client";

import { useTranslations } from "next-intl";
import { Lock } from "lucide-react";
import { Input, Label } from "@qrody/ui";
import {
  type AnonymousEcc,
  type AnonymousFormat,
  type AnonymousFrame,
} from "@qrody/api-client/render";
import { QR_MAX_VERSION, QR_MIN_VERSION } from "@qrody/qr-renderer";
import type { GeneratorCapabilities } from "../domain/capabilities";
import {
  DPI_PRESETS,
  ECC_OPTIONS,
  FORMATS,
  FRAME_OPTIONS,
  MM_PER_INCH,
  PX_PER_MODULE_GREEN,
  SIZE_STEP_PX,
} from "../domain/payload";
import { Field } from "../rendering/preview-pane";
import { ActionBar } from "./action-bar";
import type { SaveInputValue } from "../domain/save-mapping";
import type { QrGeneratorActionState } from "../components/qr-generator";

export interface DesignPanelProps {
  caps: GeneratorCapabilities;
  // Display + render-size. `displayMm` is read-only here — it drives the
  // DPI computation for the file-resolution slider and the scannability
  // gate, but the slider that mutates it lives in PreviewPanel (so the
  // control sits next to the QR it scales).
  displayMm: number;
  size: number;
  setSize: (next: number | ((prev: number) => number)) => void;
  currentMatrixModules: number;
  requiredMinPxForScan: number;
  dpiCapMaxPx: number;
  scanImpossibleWithoutPro: boolean;
  dynamicMinPx: number;
  dynamicMaxPx: number;
  // Design vanity (colors/frame/ecc/minVersion)
  fg: string;
  bg: string;
  frame: AnonymousFrame;
  ecc: AnonymousEcc;
  eccAvailability: Record<AnonymousEcc, boolean>;
  disableEcc: boolean;
  tryApplyVanity: (next: {
    fg?: string;
    bg?: string;
    frame?: AnonymousFrame;
    ecc?: AnonymousEcc;
  }) => boolean;
  vanityCount: number;
  vanityLimit: number | null;
  vanityAtLimit: boolean;
  minVersion: number | null;
  setMinVersion: (n: number | null) => void;
  setVanityUpgradeOpen: (open: boolean) => void;
  // Watermark
  watermarkRemoved: boolean;
  setWatermarkRemoved: (removed: boolean) => void;
  // Format
  format: AnonymousFormat;
  setFormat: (f: AnonymousFormat) => void;
  // Action-Bar
  mounted: boolean;
  hideInlineActions: boolean;
  actionSlot?: ((state: QrGeneratorActionState) => React.ReactNode) | undefined;
  actionState: QrGeneratorActionState;
  saveInput: SaveInputValue | null;
  pending: boolean;
  liveValidationOk: boolean;
  hasRendered: boolean;
  canShareFiles: boolean;
  errMessage: string | null;
  onDownload: () => void;
  onCopyImage: () => void;
  onCopyHtml: () => void;
  onShare: () => void;
  onPrint: () => void;
}

/**
 * Right column (Col 3 on lg+) — the entire "Gestaltung" surface:
 * display-size, vanity knobs (colors / frame / ECC / minVersion) gated
 * by `caps.vanityStackLimit`, watermark toggle, format picker, the
 * file-resolution slider with scannability guardrails and DPI presets,
 * and the action-bar (Download / Copy / Share / Print + optional caller-
 * supplied actionSlot).
 */
export function DesignPanel(props: DesignPanelProps) {
  const t = useTranslations("generator");
  const tEditor = useTranslations("editor.fields");
  const {
    caps,
    displayMm,
    size,
    setSize,
    currentMatrixModules,
    requiredMinPxForScan,
    scanImpossibleWithoutPro,
    dynamicMinPx,
    dynamicMaxPx,
    fg,
    bg,
    frame,
    ecc,
    eccAvailability,
    disableEcc,
    tryApplyVanity,
    vanityCount,
    vanityLimit,
    vanityAtLimit,
    minVersion,
    setMinVersion,
    setVanityUpgradeOpen,
    watermarkRemoved,
    setWatermarkRemoved,
    format,
    setFormat,
    mounted,
    hideInlineActions,
    actionSlot,
    actionState,
    saveInput,
    pending,
    liveValidationOk,
    hasRendered,
    canShareFiles,
    errMessage,
    onDownload,
    onCopyImage,
    onCopyHtml,
    onShare,
    onPrint,
  } = props;

  const currentDpi = displayMm > 0 ? Math.round((size * MM_PER_INCH) / displayMm) : 0;
  const pxPerModule = currentMatrixModules > 0 ? size / currentMatrixModules : 0;
  const pxPerModuleToneClass =
    pxPerModule >= PX_PER_MODULE_GREEN
      ? "text-emerald-600 dark:text-emerald-400"
      : pxPerModule >= 2
        ? "text-amber-600 dark:text-amber-400"
        : "text-rose-600 dark:text-rose-400";
  const requiredDpiForScan =
    displayMm > 0 ? Math.ceil((requiredMinPxForScan * MM_PER_INCH) / displayMm) : 0;
  const sliderDisabled = scanImpossibleWithoutPro;
  const downloadDisabled = pending || scanImpossibleWithoutPro;

  // Preset clamped strictly auf das dynamische Slider-Intervall — auch
  // auf den scanability-Floor. Anonymous: 600 DPI ist paywalled (Klick
  // öffnet Dialog).
  const applyDpi = (dpi: number) => {
    const exact = Math.round((displayMm * dpi) / MM_PER_INCH);
    const clamped = Math.max(dynamicMinPx, Math.min(dynamicMaxPx, exact));
    setSize(clamped);
  };

  return (
    <section
      aria-labelledby="design-heading"
      className="w-fit max-w-full space-y-4 rounded-xl border bg-card p-3 shadow-sm sm:p-4 order-4 md:order-4 md:col-span-2 lg:col-span-1 lg:col-start-3 lg:row-start-1"
    >
      <div>
        <h2 id="design-heading" className="text-sm font-semibold">
          {t("section.design")}
        </h2>
        <p className="text-xs text-muted-foreground">{t("section.designHint")}</p>
      </div>

      {/* Visual customisations — colors, frame, error correction.
          Bundled into a single tinted block so the slot counter and
          the knobs it counts live in the same place. */}
      <section
        aria-labelledby="pro-features-heading"
        className="w-fit max-w-full space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/30"
      >
        <div className="flex items-center justify-between gap-2">
          <h3
            id="pro-features-heading"
            className="text-sm font-semibold text-amber-900 dark:text-amber-200"
          >
            {t("pro.heading")}
          </h3>
          {vanityLimit !== null && (
            <span
              aria-live="polite"
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ${
                vanityAtLimit
                  ? "bg-orange-300 text-orange-950 dark:bg-orange-700 dark:text-orange-50"
                  : "bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100"
              }`}
            >
              {t(vanityAtLimit ? "pro.slotCounterFull" : "pro.slotCounter", {
                n: vanityCount,
                max: vanityLimit,
              })}
            </span>
          )}
        </div>
        {caps.showAccountCTAs ? (
          <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
            {t("pro.description")}
          </p>
        ) : null}

        <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
          <Field id="fg" label={t("field.foreground")}>
            <Input
              id="fg"
              type="color"
              value={fg}
              onChange={(e) => tryApplyVanity({ fg: e.target.value })}
              className="h-9 w-14 p-1"
            />
          </Field>
          <Field id="bg" label={t("field.background")}>
            <Input
              id="bg"
              type="color"
              value={bg}
              onChange={(e) => tryApplyVanity({ bg: e.target.value })}
              className="h-9 w-14 p-1"
            />
          </Field>

          <Field id="frame" label={t("field.frame")}>
            <select
              id="frame"
              className="h-9 w-auto rounded-md border bg-background px-2 text-sm"
              value={frame}
              onChange={(e) => tryApplyVanity({ frame: e.target.value as AnonymousFrame })}
            >
              {FRAME_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {tEditor(`frameOption.${f}`)}
                </option>
              ))}
            </select>
          </Field>

          <Field id="ecc" label={t("field.ecc")}>
            <select
              id="ecc"
              className="h-9 w-auto rounded-md border bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              value={ecc}
              disabled={disableEcc}
              onChange={(e) => {
                const next = e.target.value as AnonymousEcc;
                const ok = tryApplyVanity({ ecc: next });
                if (!ok) e.currentTarget.value = ecc;
              }}
            >
              {ECC_OPTIONS.map((e) => (
                <option key={e} value={e} disabled={!eccAvailability[e]}>
                  {t(`eccOption.${e}`)}
                  {!eccAvailability[e] ? " — zu lang" : ""}
                </option>
              ))}
            </select>
            <p className="mt-1 max-w-[16rem] text-xs text-amber-900/70 dark:text-amber-200/70">
              {t(`eccHint.${ecc}`)}
            </p>
          </Field>

          {/* Power-Option: Mindestversion. */}
          <Field id="minVersion" label="Mindestversion (optional)">
            {caps.allowMinVersion ? (
              <div className="flex items-center gap-2">
                <input
                  id="minVersion"
                  type="number"
                  min={QR_MIN_VERSION}
                  max={QR_MAX_VERSION}
                  step={1}
                  value={minVersion ?? ""}
                  placeholder="auto"
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setMinVersion(null);
                      return;
                    }
                    const n = Number(raw);
                    if (!Number.isFinite(n)) return;
                    const clamped = Math.min(
                      QR_MAX_VERSION,
                      Math.max(QR_MIN_VERSION, Math.round(n)),
                    );
                    setMinVersion(clamped);
                  }}
                  className="h-9 w-20 rounded-md border bg-background px-2 text-sm"
                />
                {minVersion !== null ? (
                  <button
                    type="button"
                    onClick={() => setMinVersion(null)}
                    className="shrink-0 text-xs text-amber-900/70 underline-offset-2 hover:underline dark:text-amber-200/70"
                  >
                    auto
                  </button>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setVanityUpgradeOpen(true)}
                title="Mindestversion erzwingen ist Pro-Feature — Subscription anlegen, um es freizuschalten"
                aria-label="Mindestversion (Pro-Feature)"
                className="flex h-9 w-fit cursor-pointer items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 text-sm text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
              >
                <span className="inline-flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" aria-hidden />
                  auto
                </span>
                <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-800/60 dark:text-amber-100">
                  {t("action.proRequired")}
                </span>
              </button>
            )}
            <p className="mt-1 max-w-[16rem] text-xs text-amber-900/70 dark:text-amber-200/70">
              1–40. Auto wählt die kleinste passende Version. Höher = mehr Module für
              konsistente Größen oder größere Scan-Distanz.
            </p>
          </Field>
        </div>
      </section>

      {/* Watermark-Toggle + Format side-by-side so the row stays content-width. */}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        {caps.allowWatermarkRemoval ? (
          <label className="flex w-fit cursor-pointer items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60">
            <span className="inline-flex items-center gap-2">{t("action.watermarkRemove")}</span>
            <input
              type="checkbox"
              checked={watermarkRemoved}
              onChange={(e) => setWatermarkRemoved(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
          </label>
        ) : (
          <button
            type="button"
            onClick={() => setVanityUpgradeOpen(true)}
            title={t("action.watermarkProTooltip")}
            className="flex w-fit cursor-pointer items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
          >
            <span className="inline-flex items-center gap-2">
              <Lock className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" aria-hidden />
              {t("action.watermarkRemove")}
            </span>
            <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-800/60 dark:text-amber-100">
              {t("action.proRequired")}
            </span>
          </button>
        )}

        <Field id="format" label={t("field.format")}>
          <select
            id="format"
            className="h-9 w-auto rounded-md border bg-background px-2 text-sm"
            value={format}
            onChange={(e) => setFormat(e.target.value as AnonymousFormat)}
          >
            {FORMATS.map((f) => (
              <option key={f} value={f}>
                {f.toUpperCase()}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="space-y-2 pt-1">
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <Label htmlFor="size" className="text-sm">
              {t("field.size")}
            </Label>
            <span aria-live="polite" className="text-xs tabular-nums">
              <span className="text-muted-foreground">
                {size} {t("field.sizeUnit")}
              </span>
              {pxPerModule > 0 && (
                <span className={`ml-2 font-medium ${pxPerModuleToneClass}`}>
                  {pxPerModule.toFixed(1)} px/M
                </span>
              )}
              {currentDpi > 0 && (
                <span className="ml-2 text-muted-foreground">≈ {currentDpi} DPI</span>
              )}
            </span>
          </div>
          <input
            id="size"
            type="range"
            min={dynamicMinPx}
            max={dynamicMaxPx}
            step={SIZE_STEP_PX}
            value={size}
            disabled={sliderDisabled}
            onChange={(e) => setSize(Number(e.target.value))}
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:cursor-not-allowed disabled:opacity-50"
            aria-valuemin={dynamicMinPx}
            aria-valuemax={dynamicMaxPx}
            aria-valuenow={size}
          />
        </div>
        {scanImpossibleWithoutPro && (
          <div
            role="alert"
            className="rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
          >
            <p className="font-semibold">{t("scanWarning.title")}</p>
            <p className="mt-1">
              {t("scanWarning.body", {
                mm: displayMm,
                required: requiredMinPxForScan,
                requiredDpi: requiredDpiForScan,
              })}
            </p>
            <button
              type="button"
              onClick={() => setVanityUpgradeOpen(true)}
              className="mt-2 font-semibold underline-offset-2 hover:underline"
            >
              {t("scanWarning.cta")}
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t("field.dpiPresetLabel")}:</span>
          {DPI_PRESETS.map((dpi) => {
            const locked = dpi > caps.maxDpi;
            return (
              <button
                key={dpi}
                type="button"
                onClick={() => (locked ? setVanityUpgradeOpen(true) : applyDpi(dpi))}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-medium tabular-nums transition-colors hover:bg-muted ${
                  locked
                    ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                    : "bg-background"
                }`}
                title={
                  locked
                    ? t("field.dpiPresetProTooltip", { dpi })
                    : t("field.dpiPresetTooltip", { dpi, mm: displayMm })
                }
              >
                {dpi}
                {locked && (
                  <Lock className="h-3 w-3 text-amber-700 dark:text-amber-300" aria-hidden />
                )}
              </button>
            );
          })}
        </div>

        <ActionBar
          caps={caps}
          format={format}
          scanImpossibleWithoutPro={scanImpossibleWithoutPro}
          saveInput={saveInput}
          liveValidationOk={liveValidationOk}
          hasRendered={hasRendered}
          pending={pending}
          canShareFiles={canShareFiles}
          mounted={mounted}
          hideInlineActions={hideInlineActions}
          actionSlot={actionSlot}
          actionState={actionState}
          onDownload={onDownload}
          onCopyImage={onCopyImage}
          onCopyHtml={onCopyHtml}
          onShare={onShare}
          onPrint={onPrint}
        />
      </div>

      {errMessage && (
        <p role="alert" className="text-sm text-destructive">
          {errMessage}
        </p>
      )}
    </section>
  );
}
